import { format } from 'date-fns';
import { useCallback, useState } from 'react';
import { Box, Column, Row, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { SolidExclamationWarningAlertIcon } from '@tempmee/react-icons';
import { Divider, InputSelect } from '~/commonComponents';
import { AddMoreOnsiteInterviewButton } from '~/components/PermanentJobsV2/components';
import { JobPostingStatus } from '~/domain/PermanentJobs';
import { firePermJobOverviewAddInterviewsClicked } from '~/posthogs/events';
import { ClosedBanner } from './ClosedBanner';
import { JobPostingStatusOptions } from './Constants';
import { JobStatusInfo } from './JobStatusInfo';
import { SuspendedBanner } from './SuspendedBanner';

interface IJobOverviewHeaderProps {
  id: string;
  officeName: string;
  professional: string;
  status: JobPostingStatus;
  title: string;
  closedAt?: Date;
  hasScheduledOnsiteInterviews?: boolean;
  postedAt?: Date;
  suspendedAt?: Date;
  officeId?: string;
  officePlanId?: string;
  onCloseJob?(): void;
}

export const JobOverviewHeader = ({
  closedAt,
  hasScheduledOnsiteInterviews = false,
  id,
  officeName,
  postedAt,
  professional,
  status,
  suspendedAt,
  officeId,
  officePlanId,
  title,
  onCloseJob,
}: IJobOverviewHeaderProps) => {
  const [jobStatus, setJobStatus] = useState<JobPostingStatus>(status);

  const isPessimisticActive = status === JobPostingStatus.Active;
  const isPessimisticClosed = status === JobPostingStatus.Closed;
  const isPessimisticSuspended = status === JobPostingStatus.Suspended;

  const isClosed = isPessimisticClosed && closedAt;
  const isSuspended = isPessimisticSuspended && suspendedAt;
  const showCloseOrSuspendDate = isClosed || isSuspended;

  const handleJobStatusChanges = useCallback(
    (newStatus: string) => {
      const status = newStatus as JobPostingStatus;

      if (status === JobPostingStatus.Closed) {
        onCloseJob?.();

        return;
      }

      // Currently, only "Closed" status is supported, not possible to update to other statuses
      setJobStatus(status);
    },
    [onCloseJob],
  );

  return (
    <Column className="mt-8" style={{ marginInline: 115 }}>
      <Row justify="between">
        <Column>
          <Row align="center" gap={2}>
            <Typography as="span" bold size="heading_4">
              {title}
            </Typography>
            {isPessimisticSuspended && <SolidExclamationWarningAlertIcon color={colors.warning[500]} size={24} />}
          </Row>
          <Typography as="span">
            <Typography as="span" bold>Professional Type</Typography>: {professional}
          </Typography>
          <Typography as="span">
            <Typography as="span" bold>Office</Typography>: {officeName}
          </Typography>
          <Row align="center" gap={4}>
            <Typography as="span">
              <Typography as="span" bold>Posted on</Typography>: {postedAt ? format(postedAt, 'MMM dd, yyyy') : ''}
            </Typography>
            {showCloseOrSuspendDate && (
              <>
                <Divider isRounded height="100%" marginVertical={0} width={2} />
                {isClosed && (
                  <Typography as="span">
                    <Typography as="span" bold>Closed on</Typography>: {format(closedAt, 'MMM dd, yyyy')}
                  </Typography>
                )}
                {isSuspended && (
                  <Typography as="span">
                    <Typography as="span" bold>Suspended on</Typography>: {format(suspendedAt, 'MMM dd, yyyy')}
                  </Typography>
                )}
              </>
            )}
          </Row>
        </Column>
        <Column align="end" justify="between">
          {isPessimisticActive ? (
            <Row align="center" gap={2}>
              <Typography as="span" bold variant="body1" style={{ textWrap: 'nowrap' }}>
                Job Status<Typography as="span">:</Typography>
              </Typography>
              <Box width="180px">
                <InputSelect
                  dataCy="job-status-select"
                  isSelectVisible={false}
                  options={JobPostingStatusOptions}
                  value={jobStatus}
                  setValue={handleJobStatusChanges}
                />
              </Box>
            </Row>
          ) : (
            <JobStatusInfo jobStatus={status} />
          )}
          {isPessimisticActive && (
            <AddMoreOnsiteInterviewButton
              jobPostingId={id}
              variant="lite"
              onTrack={() => firePermJobOverviewAddInterviewsClicked({ postingId: id })}
            />
          )}
        </Column>
      </Row>

      <ClosedBanner
        hasScheduledOnsiteInterviews={hasScheduledOnsiteInterviews}
        jobStatus={status}
      />
      <SuspendedBanner jobStatus={status} officeId={officeId} officePlanId={officePlanId} />

      <Box px={10} className="mb-2">
        <Divider marginVertical={22} />
      </Box>
    </Column>
  );
};
